import type { ReactNode } from 'react';
import { IconSortAsc, IconSortDesc, IconSortNeutral } from './icons';
import { Skeleton } from './skeleton';
import { cn, focusRing } from './utils';

export type SortDirection = 'asc' | 'desc';

export interface SortState<TKey extends string = string> {
  readonly by: TKey;
  readonly dir: SortDirection;
}

export interface TableColumn<TRow, TKey extends string = string> {
  readonly id: string;
  readonly header: ReactNode;
  /** Chave de ordenação enviada à API. Ausente = coluna não ordenável. */
  readonly sortKey?: TKey;
  readonly align?: 'left' | 'right' | 'center';
  readonly width?: string;
  readonly cell: (row: TRow, index: number) => ReactNode;
  /** Cabeçalho textual para leitor de tela quando `header` é só ícone. */
  readonly srHeader?: string;
  readonly className?: string;
}

export interface TableProps<TRow, TKey extends string = string> {
  readonly columns: ReadonlyArray<TableColumn<TRow, TKey>>;
  readonly rows: readonly TRow[];
  readonly rowKey: (row: TRow) => string;
  /** Descrição da tabela para tecnologia assistiva. Obrigatório. */
  readonly caption: string;
  /** Mostra a legenda visualmente além de expô-la ao leitor de tela. */
  readonly showCaption?: boolean;
  readonly sort?: SortState<TKey>;
  readonly onSortChange?: (sort: SortState<TKey>) => void;
  readonly loading?: boolean;
  readonly skeletonRows?: number;
  readonly empty?: ReactNode;
  readonly className?: string;
  readonly rowClassName?: (row: TRow) => string | undefined;
  /** Densidade: `compact` na Lista Mestra, `comfortable` em listas curtas. */
  readonly density?: 'compact' | 'comfortable';
}

/**
 * Tabela de dados.
 *
 * ACESSIBILIDADE:
 *  · `<caption>` obrigatório — sem ele, quem navega por tabelas no leitor de
 *    tela recebe "tabela com N colunas" e nada mais;
 *  · o estado de ordenação vai em `aria-sort` NO `<th>` (não no botão), que é
 *    onde a especificação manda;
 *  · o botão de ordenação tem `aria-label` que descreve a AÇÃO ("Ordenar por
 *    Nome, crescente"), não o estado — o estado já está no `aria-sort`;
 *  · durante o carregamento usamos esqueleto e `aria-busy`, mantendo o número
 *    de linhas estável para não deslocar o foco de quem estiver navegando.
 */
export function Table<TRow, TKey extends string = string>({
  columns,
  rows,
  rowKey,
  caption,
  showCaption = false,
  sort,
  onSortChange,
  loading = false,
  skeletonRows = 8,
  empty,
  className,
  rowClassName,
  density = 'compact',
}: TableProps<TRow, TKey>) {
  const cellPadding = density === 'compact' ? 'px-3 py-2' : 'px-4 py-3';

  return (
    <div className={cn('w-full overflow-x-auto rounded-lg border border-border bg-surface', className)}>
      <table className="w-full border-collapse text-sm" aria-busy={loading || undefined}>
        <caption
          className={cn(
            'text-left text-sm text-content-muted',
            showCaption ? 'px-3 py-2' : 'qc-sr-only',
          )}
        >
          {caption}
        </caption>

        <thead>
          <tr className="border-b border-border bg-surface-sunken">
            {columns.map((column) => {
              const isSorted = sort && column.sortKey && sort.by === column.sortKey;
              const ariaSort: 'ascending' | 'descending' | 'none' | undefined = column.sortKey
                ? isSorted
                  ? sort.dir === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : 'none'
                : undefined;

              return (
                <th
                  key={column.id}
                  scope="col"
                  aria-sort={ariaSort}
                  style={column.width ? { width: column.width } : undefined}
                  className={cn(
                    'text-xs font-semibold uppercase tracking-wide text-content-muted',
                    cellPadding,
                    column.align === 'right' && 'text-right',
                    column.align === 'center' && 'text-center',
                    (!column.align || column.align === 'left') && 'text-left',
                    column.className,
                  )}
                >
                  {column.sortKey && onSortChange ? (
                    <button
                      type="button"
                      onClick={() =>
                        onSortChange({
                          by: column.sortKey as TKey,
                          dir: isSorted && sort.dir === 'asc' ? 'desc' : 'asc',
                        })
                      }
                      aria-label={`Ordenar por ${column.srHeader ?? textOf(column.header)}, ${
                        isSorted && sort.dir === 'asc' ? 'decrescente' : 'crescente'
                      }`}
                      className={cn(
                        'inline-flex items-center gap-1 rounded uppercase tracking-wide hover:text-content',
                        // 24px de ALTURA DE ALVO com padding negativo na margem:
                        // o cabeçalho continua com a mesma aparência, e a área
                        // clicável passa a atender WCAG 2.5.8. Media 18px, que é
                        // pouco para um clique preciso numa tabela densa — e
                        // menos ainda no toque.
                        'min-h-6 -my-0.5 py-0.5',
                        isSorted && 'text-brand',
                        focusRing,
                      )}
                    >
                      <span>{column.header}</span>
                      {isSorted ? (
                        sort.dir === 'asc' ? (
                          <IconSortAsc size={13} />
                        ) : (
                          <IconSortDesc size={13} />
                        )
                      ) : (
                        <IconSortNeutral size={13} className="opacity-45" />
                      )}
                    </button>
                  ) : (
                    <>
                      {column.header}
                      {column.srHeader ? <span className="qc-sr-only">{column.srHeader}</span> : null}
                    </>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {loading ? (
            Array.from({ length: skeletonRows }, (_, index) => (
              <tr key={`skeleton-${index}`} className="border-b border-border-subtle">
                {columns.map((column) => (
                  <td key={column.id} className={cellPadding}>
                    <Skeleton className="h-4 w-full max-w-[14rem]" />
                  </td>
                ))}
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="p-0">
                {empty ?? (
                  <p className="px-4 py-10 text-center text-sm text-content-subtle">
                    Nenhum registro encontrado.
                  </p>
                )}
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr
                key={rowKey(row)}
                className={cn(
                  'border-b border-border-subtle last:border-b-0',
                  'hover:bg-surface-sunken focus-within:bg-surface-sunken',
                  rowClassName?.(row),
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={cn(
                      'align-middle text-content',
                      cellPadding,
                      column.align === 'right' && 'text-right',
                      column.align === 'center' && 'text-center',
                      column.className,
                    )}
                  >
                    {column.cell(row, index)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Extrai texto de um ReactNode simples para compor rótulos ARIA. */
function textOf(node: ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  return 'coluna';
}

/** Célula de ações — agrupa botões e reserva largura estável. */
export function TableActions({
  children,
  label = 'Ações do registro',
}: {
  readonly children: ReactNode;
  readonly label?: string;
}) {
  return (
    <div role="group" aria-label={label} className="flex items-center justify-end gap-1">
      {children}
    </div>
  );
}
